<template>
  <div>
    <template v-if="(isApplicationTemplateLoading || isApplicationLoading) && !currentForm">
      <Loading />
    </template>

    <template v-if="currentForm">
      <!-- START Scam Warning Modal -->
      <ScamWarningModal :showModal="showScamModal" @close="toggleScamModal" />
      <!-- END Scam Warning Modal -->

      <!-- START Inactive Modal -->
      <InactiveModal :showModal="inactiveModal" @close="toggleInactiveModal" />
      <!-- END Inactive Modal -->

      <!-- START Self Apply Modal -->
      <SelfApplyModal :showModal="selfApplyModal" @close="toggleSelfApplyModal" />
      <!-- END Self Apply Modal -->

      <PreviewModeWarningModal :showModal="previewModeModal" @close="togglePreviewModeModal" />

      <!-- START SUB NAV BAR -->
      <template v-if="!started">
        <ApplySubNav
          class="apply-sub-nav"
          active="application"
          :verify="currentForm.need_applicant_experian_authtoken"
          :pay="currentForm.price > 0"
          :preview="preview"
          :income="currentForm.need_applicant_finicity_bank_connection"
        />
      </template>
      <template v-else>
        <div
          class="hidden md:block w-full bg-white py-5 px-5 shadow-lg apply-sub-nav flex flex-col"
        >
          <div class="w-36">
            <SecondaryButton :classes="'border-brand-mid-gray'" v-if="preview" @click="exitPreview">
              <div class="flex items-center">
                <ChevronLeftIcon class="text-brand-green w-5 h-5 mr-1" />
                <p class="text-sm">Exit preview</p>
              </div>
            </SecondaryButton>
          </div>
          <div class="md:w-3/4 m-auto">
            <div class="py-2">
              <h5 class="text-brand-green text-lg">Application</h5>
            </div>
            <div>
              <template
                v-for="(section, sectionIndex) in currentForm.form.sections"
                :key="`${section.section_id}-${sectionIndex}`"
              >
                <p
                  :class="[
                    'section-label inline cursor-pointer text-sm relative',
                    sectionIndex === currentSectionIndex
                      ? 'text-brand-black'
                      : 'text-brand-mid-gray'
                  ]"
                  @click="setCurrentSection(sectionIndex)"
                >
                  {{ section.label }}
                </p>

                <AlertDot :ping="true" v-if="section.isValid === false" />

                <p
                  class="text-sm inline mx-2 text-brand-dark-gray"
                  v-if="sectionIndex !== lastSectionIndex"
                >
                  |
                </p>
              </template>
            </div>
          </div>
        </div>
        <div class="block md:hidden">
          <ApplySubNav
            active="application"
            :verify="currentForm.need_applicant_experian_authtoken"
            :income="currentForm.need_applicant_finicity_bank_connection"
            :pay="currentForm.price > 0"
            :preview="preview"
          />
        </div>
      </template>
      <!-- END SUB NAV BAR -->

      <!-- START APPLY CONTENT -->
      <div class="h-full py-5 md:py-10">
        <div class="max-w-screen-md mx-auto">
          <!-- START Start Application View -->
          <template v-if="!started">
            <ErrorToast v-if="iframeToast">
              <h2 class="text-brand-red text-opacity-80 py-2">Warning: Possible Security Risk</h2>
              <p class="py-2 text-brand-red text-opacity-80">
                This site is using software to display the content below from localhost. This is NOT
                the original application page. Rent Application is not able to identify whether this
                site is secure. For security reasons, we do not recommend using this site to apply
                as it may put your private information at risk.
              </p>
              <p class="py-2 text-brand-red text-opacity-80 font-semi-bold">
                You can access a secure version of this application here:
                <a :href="`${host}/${currentForm.slug}`" class="text-brand-blue"
                  >{{ host }}/{{ currentForm.slug }}</a
                >
              </p>
            </ErrorToast>
            <div class="w-11/12 mx-auto my-3">
              <AlertToast v-if="hasStoredForm">
                <div class="py-5 px-5">
                  <p class="text-brand-black pb-3">
                    Looks like you have unsubmitted answers from previous session. Want to restore
                    the answers?
                  </p>
                  <div class="flex flex-col md:flex-row md:justify-center">
                    <div class="flex w-full md:w-1/3 mb-3 md:mr-5 md:mb-0">
                      <SecondaryButton @click="removeFormInLocalStorage">No</SecondaryButton>
                    </div>
                    <div class="flex w-full md:w-1/3">
                      <PrimaryButton @click="applyFormInLocalStorage">Yes</PrimaryButton>
                    </div>
                  </div>
                </div>
              </AlertToast>
            </div>

            <SectionContainer class="landlord-description">
              <div class="flex flex-col">
                <div
                  class="flex py-3 mr-2 landlord-description-item"
                  v-if="currentForm.landlord.logo && !logoImageError"
                  style="max-width: 200px"
                >
                  <img
                    :src="currentForm.landlord.logo"
                    class="w-full h-auto"
                    @error="logoImageError = true"
                  />
                </div>
                <h2
                  class="text-xl font-semibold capitalize pb-2"
                  v-if="currentForm.landlord.company_name"
                >
                  {{ currentForm.landlord.company_name }}
                </h2>
              </div>

              <div
                class="flex items-center"
                v-if="currentForm.landlord.name && !currentForm.landlord.in_company"
              >
                <div class="flex items-center justify-center mr-2">
                  <Gravatar
                    :photo="currentForm.landlord.profile_photo"
                    :displayName="formatGravatar"
                    :color="`bg-palette-gravatar-1`"
                    :displayCutout="false"
                    :zIndex="1"
                  />
                </div>

                <div class="flex flex-col">
                  <p class="italic text-brand-dark-gray text-sm">Listed by</p>
                  <p>
                    {{ currentForm.landlord.name }}
                  </p>
                </div>
              </div>

              <div class="flex py-3 landlord-description-item" v-if="currentForm.landlord.address">
                <LocationMarkerIcon class="text-brand-blue mr-5 w-7 h-7" />
                <p>
                  {{ currentForm.landlord.address }}
                </p>
              </div>
              <div class="flex py-3 landlord-description-item" v-if="currentForm.landlord.phone">
                <PhoneIcon class="text-brand-blue mr-5 w-7 h-7" />
                <p>
                  {{ currentForm.landlord.phone }}
                </p>
              </div>
              <div
                class="flex py-3 landlord-description-item"
                v-if="currentForm.landlord.display_email"
              >
                <MailOpenIcon class="text-brand-blue mr-5 w-7 h-7" />
                <p>
                  {{ currentForm.landlord.display_email }}
                </p>
              </div>
              <div
                class="flex py-3 landlord-description-item"
                v-if="currentForm.landlord.company_name"
              >
                <OfficeBuildingIcon class="text-brand-blue mr-5 w-7 h-7" />
                <p>
                  {{ currentForm.landlord.company_name }}
                </p>
              </div>

              <div class="flex py-3 landlord-description-item" v-if="currentForm.landlord.website">
                <GlobeIcon class="text-brand-blue mr-5 w-7 h-7" />
                <p>
                  {{ currentForm.landlord.website }}
                </p>
              </div>

              <Line />
              <div class="py-3 flex flex-row w-full application-fee">
                <div class="flex-1">
                  <p class="font-semibold">Application Fee</p>
                </div>
                <div class="flex-1 text-right">
                  <p class="font-semibold">${{ currentForm.price }}</p>
                </div>
              </div>
            </SectionContainer>

            <div class="my-5 w-11/12 mx-auto">
              <AlertToast>
                <div class="px-5 py-8">
                  <div class="flex mb-5">
                    <img src="@/assets/icons/lock-yellow.svg" class="mr-5" />
                    <p>SSNs and credit cards are never visible to landlords</p>
                  </div>

                  <div class="flex">
                    <InformationCircleIcon class="w-9 h-9 mr-3 text-brand-yellow" />

                    <p class="font-normal">
                      Any credit reports requested through Rent Application are a soft inquiry, and
                      will not affect or lower your credit score.
                    </p>
                  </div>
                </div>
              </AlertToast>
            </div>

            <div class="mx-auto w-80 pt-8">
              <PrimaryButton @click="startApplication">Start application</PrimaryButton>
            </div>
          </template>
          <!-- END Start Application View -->

          <template v-else>
            <template
              v-for="(section, sectionIndex) in currentForm.form.sections"
              :key="`${section.section_id}-${sectionIndex}`"
            >
              <SectionContainer
                v-show="sectionIndex === currentSectionIndex"
                class="current-section"
              >
                <div class="my-5">
                  <h2 class="text-brand-black text-xl font-semibold current-section-label">
                    {{ section.label }}
                  </h2>
                </div>

                <div
                  v-for="field in section.fields"
                  :key="field.name"
                  :ref="`field-container-${field.name}`"
                >
                  <FieldDisplay
                    :field="field"
                    @update="updateFieldValue(field, $event)"
                    @validated_address="field.validated_address = $event"
                    :ref="field.name"
                    mode="edit"
                  />
                </div>

                <div class="mx-auto w-80 pt-8">
                  <PrimaryButton
                    v-if="sectionIndex !== lastSectionIndex && !isApplicationLoading"
                    @click="setCurrentSection(sectionIndex + 1, true)"
                    >Next</PrimaryButton
                  >
                  <PrimaryButton
                    v-if="sectionIndex === lastSectionIndex || isApplicationLoading"
                    :is-loading="isApplicationLoading"
                    :disabled="isApplicationLoading || isSample"
                    @click="submit"
                    >{{
                      mode === "edit" ? "Update application" : "Submit application"
                    }}</PrimaryButton
                  >
                  <p
                    v-if="sectionIndex !== 0"
                    class="py-5 text-center cursor-pointer text-brand-green"
                    @click="setCurrentSection(sectionIndex - 1)"
                  >
                    Previous
                  </p>
                </div>
              </SectionContainer>

              <div
                class="my-4 block md:hidden"
                v-show="sectionIndex !== currentSectionIndex"
                @click="setCurrentSection(sectionIndex)"
              >
                <div
                  class="max-w-screen-md mx-auto bg-white px-3 py-3 rounded-md shadow-lg"
                  style="width: 95%"
                >
                  <div class="my-2 flex flex-row space-between">
                    <div class="flex-1 flex flex-row">
                      <CheckIcon
                        class="h-7 text-brand-blue mr-2"
                        v-if="section.touched && section.isValid"
                      />
                      <h2 class="text-brand-black text-xl">
                        {{ section.label }}
                      </h2>
                    </div>
                    <div class="flex-4 flex justify-end">
                      <ChevronDownIcon class="h-8 w-8 text-brand-green" />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import hotkeys from "hotkeys-js"
import { Chance } from "chance"
import moment from "moment"
import _ from "lodash"

import { mapGetters } from "vuex"
import { APPLICATION_UPDATE } from "@/store/types"

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  CheckIcon,
  LocationMarkerIcon,
  PhoneIcon,
  MailOpenIcon,
  OfficeBuildingIcon,
  GlobeIcon,
  InformationCircleIcon
} from "@heroicons/vue/outline"

import ScamWarningModal from "./components/ScamWarningModal.vue"
import InactiveModal from "./components/InactiveModal.vue"
import SelfApplyModal from "./components/SelfApplyModal.vue"
import PreviewModeWarningModal from "./components/PreviewModeWarningModal.vue"

// TODO: Create deep copy of apptemp.form or app.form before editing
export default {
  components: {
    ChevronDownIcon,
    CheckIcon,
    LocationMarkerIcon,
    PhoneIcon,
    MailOpenIcon,
    OfficeBuildingIcon,
    GlobeIcon,
    InformationCircleIcon,
    ChevronLeftIcon,
    ScamWarningModal,
    InactiveModal,
    SelfApplyModal,
    PreviewModeWarningModal
  },
  computed: {
    ...mapGetters([
      "isApplicationTemplateLoading",
      "getCurrentApplicationTemplate",
      "isApplicationLoading",
      "getCurrentApplication",
      "isAuthenticated",
      "getCurrentUser",
      "getApplicationTemplateError",
      "getApplicationTemplateTemporary"
    ]),
    isSample() {
      if (this.$route.name === "apply_sample") {
        return true
      }
      if (
        this.getCurrentApplicationTemplate &&
        ["beta_default", "sample@rentapplication.net"].includes(
          this.getCurrentApplicationTemplate.slug
        )
      ) {
        return true
      }
      return false
    },
    hasStoredForm() {
      /*
        To force refresh this computed property
        after removing form from localstorage
      */
      this.refreshKey

      if (!this.currentForm) {
        return false
      }

      if (localStorage.getItem(this.currentForm.slug)) {
        const storedForm = JSON.parse(localStorage.getItem(this.currentForm.slug))

        if (moment(storedForm.createdAt).add(3, "days") > moment()) {
          return true
        } else {
          // Remove if data expired
          this.removeFormInLocalStorage()
          return false
        }
      }
      return false
    },
    isIframe() {
      let iframe = false
      try {
        iframe = window.top != window
      } catch (error) {
        iframe = true
      }
      return iframe
    },
    lastSectionIndex() {
      if (this.isApplicationTemplateLoading || this.isApplicationLoading || !this.currentForm) {
        return null
      }
      return this.currentForm.form.sections.length - 1
    },
    currentSection() {
      return this.currentForm.form.sections[this.currentSectionIndex]
    },
    isAllSectionValid() {
      const that = this

      const isValid = this.currentForm.form.sections
        .map((s) => {
          return s.fields.map((f) => {
            let fieldEl = that.getFieldRef(f)
            return fieldEl.isValid
          })
        })
        .flat()
        .every(Boolean)

      return isValid
    },
    isCurrentSectionValid() {
      const that = this

      const isValid = this.currentSection.fields.every((field) => {
        let fieldEl = that.getFieldRef(field)
        return fieldEl.isValid
      })
      return isValid
    },
    firstSectionIndexWithError() {
      const that = this
      const firstSectionIndex = _.findIndex(
        this.currentForm.form.sections.map((s) => {
          return s.fields.filter((f) => {
            let fieldEl = that.getFieldRef(f)
            return !fieldEl.isValid
          })
        }),
        (s) => s.length
      )
      return firstSectionIndex
    },
    formatGravatar() {
      if (!this.currentForm) {
        return ""
      }
      if (this.currentForm.landlord.name) {
        if (this.currentForm.landlord.name.split(" ").length > 1) {
          return `${this.currentForm.landlord.name.split(" ")[0][0]}${
            this.currentForm.landlord.name.split(" ")[1][0]
          }`
        } else {
          return `${this.currentForm.landlord.name.substring(0, 2)}`
        }
      } else {
        return this.currentForm.landlord.display_email.substring(0, 2)
      }
    }
  },
  watch: {
    getCurrentApplicationTemplate: {
      handler(val) {
        if (val) {
          if (this.$route.params.slug || this.$route.meta.slug) {
            // TODO: Refactor this: This is not a good way to mutate vuex state
            this.currentForm = val
          }

          if (!val.active) {
            this.toggleInactiveModal()
          } else {
            if (
              val.possible_scam &&
              !!(this.$route.params.slug || this.$route.meta.slug) &&
              (this.$route.params.slug || this.$route.meta.slug).indexOf("beta_default") < 0
            ) {
              this.toggleScamModal()
            }

            if (this.isIframe) {
              this.toggleIframeToast()
            }
          }

          if (this.preview && this.getApplicationTemplateTemporary) {
            this.currentForm = this.getApplicationTemplateTemporary
          }
        }
      }
    },
    getCurrentApplication: {
      handler(val) {
        if (val) {
          // TODO: Refactor this: This is not a good way to mutate vuex state
          this.currentForm = val
        }
      }
    }
  },
  mounted() {
    this.host = location.origin
    if (this.$route.params.id) {
      this.mode = "edit"
      this.started = true
    } else {
      if (this.getCurrentApplicationTemplate && !this.currentForm) {
        this.currentForm = this.getCurrentApplicationTemplate
      }
    }

    if (this.$route.query.preview) {
      this.preview = true

      if (this.getApplicationTemplateTemporary) {
        this.currentForm = _.cloneDeep(this.getApplicationTemplateTemporary)
      }
    }

    this.mapHotKeys()
    /* Open self apply modal if
      - authenticated user owns the apptemplate && $rootScope.user.landlord
      - apptemplate is not a sample
      - apptemplate created in less than 24 hours
    */

    this.$watch(
      (vm) => [vm.getCurrentUser, vm.getCurrentApplicationTemplate],
      (val, oldVal) => {
        if (oldVal && oldVal.filter(Boolean).length <= 1 && val.filter(Boolean).length > 1) {
          if (
            this.getCurrentUser.username === this.getCurrentApplicationTemplate.landlord.email &&
            !this.preview &&
            !this.inactiveModal
          ) {
            this.toggleSelfApplyModal()
          }
        }
      },
      { immediate: true, deep: true }
    )

    if (this.getApplicationTemplateError) {
      this.started = true
      this.currentForm = this.getCurrentApplicationTemplate

      if (this.getApplicationTemplateError.response) {
        this.setBackendError(this.getApplicationTemplateError.response.data.error)
      }
    }
  },
  data() {
    return {
      started: false,
      mode: "create",
      currentSectionIndex: 0,
      showScamModal: false,
      inactiveModal: false,
      selfApplyModal: false,
      iframeToast: false,
      host: "",
      currentForm: null,
      preview: false,
      logoImageError: false,
      refreshKey: false,
      previewModeModal: false
    }
  },
  methods: {
    mapHotKeys() {
      const that = this

      hotkeys("ctrl+8", (event) => {
        // Prevent the default refresh event under WINDOWS system
        event.preventDefault()
        const chance = new Chance()
        const now = new Date().getTime()

        const byValidator = {
          phone: () => chance.phone({ formatted: false }),
          email: () => chance.email(),
          zipcode: () => chance.zip()
        }
        const byName = {
          applicant_email: () => chance.email(),
          applicant__first_name: () => chance.first(),
          applicant__last_name: () => chance.last(),
          unit: (f) => {
            if (f.field_id === "address") {
              return JSON.stringify({
                street: chance.address(),
                city: chance.city(),
                state: "IL",
                zip: chance.zip()
              })
            } else {
              return chance.address()
            }
          },
          current_address__address1: () => chance.address(),
          current_address__city: () => chance.city(),
          current_address__state: () => chance.state(),
          current_address__zip_code: () => chance.zip(),
          current_address__landlord_email: () => chance.email(),
          previous_address_1__landlord_email: () => chance.email(),
          contact_information__phone: () => chance.phone({ formatted: false }),
          current_employer__organization_name: () => chance.name(),
          current_employer__job_title: () => "Janitor",
          current_employer__supervisor_name: () => chance.name(),
          current_employer__supervisor_phone: () => chance.phone(),
          current_employer__supervisor_email: () => chance.email()
        }

        const byId = {
          email: () => chance.email(),
          ssn: () => chance.ssn({ dashes: false }),
          date: () => moment(chance.birthday()).format("YYYY-MM-DD"),
          phone: () => chance.phone({ formatted: false }),
          choice: (f) => f.choices[Math.round(Math.random() * (f.choices.length - 1))].value,
          signature: () =>
            this.isMobile
              ? JSON.stringify(
                  [...Array(25)].map((d) => {
                    return { lx: d, ly: d, mx: d + 1, my: d + 1 }
                  })
                )
              : "Testing Testing",
          signature_new: () =>
            this.isMobile
              ? JSON.stringify([
                  {
                    color: "black",
                    points: [...Array(25)].map((d) => {
                      return { x: d, y: d, time: now + (d + 4) }
                    })
                  }
                ])
              : "Testing Testing",
          single_line: () => chance.sentence(),
          multi_line: () => chance.sentence(),
          state: () => chance.state(),
          address: () =>
            JSON.stringify({
              street: "123 Test Rd",
              city: "Chicago",
              state: "IL",
              zip: "60606"
            })
        }

        that.currentForm.form.sections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.required || Object.keys(byName).includes(field.name)) {
              field.value = (
                byValidator[field.validate] ||
                byName[field.name] ||
                byId[field.field_id] ||
                function (f) {
                  f.value
                }
              )(field)
            }
          })
        })
      })
    },
    togglePreviewModeModal() {
      this.previewModeModal = !this.previewModeModal
    },
    toggleIframeToast() {
      this.iframeToast = !this.iframeToast
    },
    toggleSelfApplyModal() {
      this.selfApplyModal = !this.selfApplyModal
    },
    toggleInactiveModal() {
      this.inactiveModal = !this.inactiveModal
    },
    toggleScamModal() {
      this.showScamModal = !this.showScamModal
    },
    startApplication() {
      this.started = true
    },
    checkSectionValid(index) {
      index = index ? index : this.currentSectionIndex

      const section = this.currentForm.form.sections[index]

      return section.fields.every((field) => {
        return !!field.error
      })
    },
    validateAllSections() {
      const that = this
      this.currentForm.form.sections.forEach((section, index) => {
        that.validateSection(index)
      })
    },
    validateSection(index) {
      const that = this
      index = index ? index : this.currentSectionIndex

      const section = this.currentForm.form.sections[index]

      section.fields.map((field) => {
        that.validateField(field)
      })
      section.touched = true
      section.isValid = section.fields.every((field) => {
        return !field.error
      })
      if (this.mode != "edit") {
        this.syncCurrentFormToLocalStorage()
      }
    },
    validateField(field) {
      if (field.field_id !== "text_display") {
        let fieldEl = this.getFieldRef(field)
        field.error = !fieldEl.isValid
        fieldEl.validateField()
      }
    },
    updateFieldValue(field, value) {
      field.value = value

      //Revalidate field if field is currently in error
      if (field.error) {
        this.validateField(field)
      }
    },
    applyFormInLocalStorage() {
      const storedForm = JSON.parse(localStorage.getItem(this.currentForm.slug))
      this.currentForm.form = storedForm.form.form

      this.started = true

      const that = this
      setTimeout(() => {
        that.validateAllSections()
        if (!that.isAllSectionValid) {
          that.currentSectionIndex = that.firstSectionIndexWithError
        }
        that.removeFormInLocalStorage()
      }, 10)
    },
    removeFormInLocalStorage() {
      localStorage.removeItem(this.currentForm.slug)
      this.refreshKey = !this.refreshKey
    },
    syncCurrentFormToLocalStorage() {
      const copiedForm = _.cloneDeep(this.currentForm)

      //Remove sensitive information
      copiedForm.form.sections.map((s) => {
        s.fields.map((f) => {
          if (f.value && (f.validate === "ssn" || f.name === "applicant__date_of_birth")) {
            f.value = null
          }
        })
      })

      localStorage.setItem(
        copiedForm.slug,
        JSON.stringify({
          createdAt: new Date(),
          form: copiedForm
        })
      )
    },
    setBackendError(errors) {
      const that = this

      this.$nextTick(function () {
        Object.keys(errors).forEach((key) => {
          const section = _.find(that.currentForm.form.sections, {
            fields: [{ name: key }]
          })
          const field = _.find(section.fields, {
            name: key
          })
          section.isValid = false
          field.error = true

          let fieldEl = that.getFieldRef(field)
          fieldEl.errorMessage = errors[key][0]
        })

        const firstSectionIndex = _.findIndex(
          that.currentForm.form.sections,
          (s) => s.isValid === false
        )

        this.setCurrentSection(firstSectionIndex, true, false)
      })
    },
    setCurrentSection(index, scrollToError = false, validateSection = true) {
      const that = this
      // If going to next section, validate
      // TODO: validate current and all sections before index if moving forward
      if (index > this.currentSectionIndex) {
        if (!this.preview) {
          if (validateSection) {
            this.validateSection(this.currentSectionIndex)
          }

          if (!this.isCurrentSectionValid) {
            if (scrollToError) {
              const section = this.currentForm.form.sections[this.currentSectionIndex]
              const firstErrorField = section.fields.find((f) => f.error)

              if (firstErrorField) {
                setTimeout(() => {
                  const fieldEl = Array.isArray(
                    that.$refs[`field-container-${firstErrorField.name}`]
                  )
                    ? that.$refs[`field-container-${firstErrorField.name}`][0]
                    : that.$refs[`field-container-${firstErrorField.name}`]

                  fieldEl.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    inline: "nearest"
                  })
                }, 10)
              }
            }
            return
          }
        }
      }
      this.currentSectionIndex = index

      if (!this.isMobile) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth"
        })
      } else {
        setTimeout(() => {
          const el = document.querySelectorAll(`.current-section h2`)[that.currentSectionIndex]
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          })
        }, 100)
      }
    },
    async submit() {
      if (this.preview) {
        this.togglePreviewModeModal()
        return
      }

      this.validateAllSections()
      const that = this

      if (!that.isAllSectionValid) {
        // Set the current section to first section with error
        this.currentSectionIndex = this.firstSectionIndexWithError
        return
      }

      if (this.$route.params.id) {
        try {
          const form = _.cloneDeep(this.currentForm.form)
          form.sections.forEach((s) => {
            s.fields.forEach((f) => {
              if (f.field_id === "yes_no" && f.value) {
                const choice = f.choices.find((c) => c.label === f.value)

                f.value = JSON.stringify({
                  value: f.value,
                  fields: choice.fields
                })
              }
            })
          })

          await this.$store.dispatch(APPLICATION_UPDATE, {
            id: this.$route.params.id,
            data: { form }
          })
          //
          if (this.getCurrentApplication.need_applicant_experian_authtoken) {
            this.$router.push({
              name: "apply_verify_disclaimer",
              params: { id: this.getCurrentApplication.id }
            })
          } else if (this.getCurrentApplication.need_applicant_finicity_bank_connection) {
            this.$router.push({
              name: "apply_verify_income",
              params: { id: this.getCurrentApplication.id }
            })
          } else if (this.getCurrentApplication.price > 0 && !this.getCurrentApplication.is_paid) {
            this.$router.push({
              name: "apply_pay",
              params: { id: this.getCurrentApplication.id }
            })
          }
          // else if (!this.getCurrentApplication.applicant.affiliate_offer_optin) {
          //   this.$router.push({
          //     name: "apply_affiliate_optin",
          //     params: { id: this.getCurrentApplication.id }
          //   })
          // }
          else {
            this.$router.push({
              name: "apply_complete",
              params: { id: this.getCurrentApplication.id }
            })
          }
        } catch (err) {
          console.log(err)
        }
      } else {
        this.$router.push({
          name: "apply_create_account",
          params: { slug: this.getCurrentApplicationTemplate.slug }
        })
      }
    },
    exitPreview() {
      this.$router.go(-1)
    },
    getFieldRef(field) {
      let fieldEl = this.$refs[field.name]
      if (Array.isArray(fieldEl)) {
        fieldEl = this.$refs[field.name][0]
      }
      return fieldEl
    }
  }
}
</script>
